<template>
  <div class="multi-metric-chart">
    <div class="chart-header">
      <h4 class="chart-title">{{ title }}</h4>
      <p class="chart-subtitle">{{ subtitle }}</p>
    </div>
    <div class="chart-container">
      <Line :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Chart.js 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  data: {
    type: Array,
    required: true
  },
  metrics: {
    type: Array,
    required: true,
    // 예: [{ key: 'total_assets', label: '자산총계', color: '#22d3ee' }, ...]
  }
});

// 숫자 문자열을 숫자로 변환 (쉼표 제거)
const parseNumber = (str) => {
  if (!str || str === '-') return 0;
  return parseInt(str.replace(/,/g, ''));
};

// 억 단위로 변환
const toHundredMillion = (value) => {
  return Math.round(value / 100000000);
};

// 데이터 정렬 (날짜순)
const sortedData = computed(() => {
  return [...props.data].sort((a, b) => {
    return a.business_year.localeCompare(b.business_year);
  });
});

// 차트 데이터
const chartData = computed(() => {
  const labels = sortedData.value.map(item => item.business_year);
  
  const datasets = props.metrics.map((metric, index) => {
    const values = sortedData.value.map(item => 
      toHundredMillion(parseNumber(item.financial[metric.key]))
    );

    return {
      label: metric.label,
      data: values,
      borderColor: metric.color,
      backgroundColor: `${metric.color}20`,
      tension: 0.4,
      fill: index === 0, // 첫 번째 메트릭만 fill
      pointRadius: 5,
      pointHoverRadius: 7,
      pointBackgroundColor: metric.color,
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    };
  });

  return {
    labels,
    datasets
  };
});

// 차트 옵션
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        },
        usePointStyle: true,
        padding: 15
      }
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      titleColor: '#fff',
      bodyColor: '#e5e7eb',
      borderColor: '#374151',
      borderWidth: 1,
      padding: 12,
      displayColors: true,
      callbacks: {
        label: function(context) {
          if (context.parsed.y === null) return null;
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          label += context.parsed.y.toLocaleString() + '억원';
          return label;
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(255, 255, 255, 0.05)',
        drawBorder: false
      },
      ticks: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(255, 255, 255, 0.05)',
        drawBorder: false
      },
      ticks: {
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        },
        callback: function(value) {
          return value.toLocaleString() + '억';
        }
      },
      title: {
        display: true,
        text: '(억원)',
        color: '#9ca3af',
        font: {
          size: 11,
          family: 'Pretendard, -apple-system, sans-serif'
        }
      }
    }
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false
  }
}));
</script>

<style scoped>
.multi-metric-chart {
  background: #1f2937;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
}

.chart-header {
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  margin: 0 0 4px 0;
}

.chart-subtitle {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.chart-container {
  height: 280px;
  position: relative;
}
</style>




